/**
 * app.aditor.ai/demo — "Uber for Ads": brief in, watch it get priced, see the
 * price (spec docs/superpowers/specs/2026-09-29-demo-a-quote-showcase-design.md
 * in aditor-ops; design contract docs/design/2026-09-29-demo-onboarding-handoff.md;
 * Alan's review 2026-09-29).
 *
 * Anonymous. The quote request starts the moment "Get quote" is tapped; the
 * showcase is choreography over real data running alongside it, never a
 * delay: the price shows at max(showcase end, quote arrived). A brief departure
 * gives the centered showcase the full stage.
 *
 * @module web/screens/demo/DemoScreen
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type DragEvent } from "react";
import { Globe } from "./Globe.tsx";
import { canQuote, hostOf, MIN_BRIEF_CHARS as MIN_BRIEF, normalizeUrl, quoteRequest, STORAGE_KEY, type IntakeState } from "./intake.ts";
import { clock, CROSSFADE_MS, LAUNCH_MS, MEADOW_LAUNCH_MS, matchPhase, productionPreview, rangeAt, stageLine, type ChainStage, type MatchPhase, type StageKey } from "./showcase.ts";
import tray from "../../assets/demo/silver-tray.webp";
import cloche from "../../assets/demo/silver-cloche.webp";
import owner from "../../assets/demo/owner-deep-work.webp";
import autoReviewIcon from "../../assets/demo/autoreview-shield.png";
import emojiReady from "../../assets/demo/emoji-ready.webp";
import emojiPage from "../../assets/demo/emoji-page.webp";
import emojiMagnifier from "../../assets/demo/emoji-magnifier.webp";
import pointingHand from "../../assets/demo/hand-2_5d.webp";
import scaleLight from "../../assets/demo/scale-light.mp4";
import scaleLightPoster from "../../assets/demo/scale-light-poster.webp";
import scaleMedium from "../../assets/demo/scale-medium.mp4";
import scaleMediumPoster from "../../assets/demo/scale-medium-poster.webp";
import scaleHeavy from "../../assets/demo/scale-heavy.mp4";
import scaleHeavyPoster from "../../assets/demo/scale-heavy-poster.webp";
import notesIcon from "../../assets/demo/source-apple-notes.webp";
import notionIcon from "../../assets/demo/source-notion.webp";
import docsIcon from "../../assets/demo/source-google-docs.webp";
import "../../demo.css";

// ---------------------------------------------------------------- data

interface QuoteLine { role: "hook" | "voiceover" | "direction"; text: string; seconds: number }
interface Quote {
  status: "quoted" | "needs_document" | "needs_scope";
  message?: string;
  totalCents?: number;
  totalMinCents?: number;
  totalMaxCents?: number;
  scope?: string;
  seconds?: number | null;
  words?: number | null;
  clips?: number | null;
  language?: string;
  lines?: QuoteLine[];
  quoteToken?: string | null;
  noChargeUntilCents?: number;
  contentOffer?: { totalCents: number; quoteToken: string } | null;
  variants?: number;
  sop?: "covered" | "partly" | "outside" | null;
  ai?: { effort: "light" | "medium" | "heavy"; level: number } | null;
}
interface NetEditor { id: string; initial: string; photoUrl?: string | null; firstName: string | null; lat: number | null; lng: number | null; rank: number; trustBattery: number; capacity: number }
interface Network { editors: NetEditor[]; stages: ChainStage[] }
interface Intake { productUrl: string | null; language: string | null; niche: string | null; briefKind: string | null }
type Body = { raw: string; seconds?: number; productUrl?: string; withContentFare?: boolean };
type Fail = { kind: "busy" | "down" | "input"; message: string };

const NICHE_CHIP: Record<string, string> = {
  beauty: "Beauty", supplements: "Supplements", health: "Health", pets: "Pets",
  fitness: "Fitness", fashion: "Fashion", gadgets: "Gadgets & home", apps: "Apps & services",
};

async function publicPost<T>(path: string, body: unknown, signal?: AbortSignal): Promise<{ status: number; body: T | null }> {
  const res = await fetch(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body), signal: signal ?? null });
  let parsed: T | null = null;
  try { parsed = (await res.json()) as T; } catch { /* non-JSON */ }
  return { status: res.status, body: parsed };
}

const eur = (cents: number) => new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Math.round(cents / 100));
const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`;

function readSaved(): Record<string, any> {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null") ?? {}; } catch { return {}; }
}
function save(patch: Record<string, unknown>): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readSaved(), ...patch })); } catch { /* private mode */ }
}

function useReducedMotion(): boolean {
  const [reduced, set] = useState(() => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches);
  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const m = matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => set(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return reduced;
}

// ---------------------------------------------------------------- screen

type Phase = "intake" | "launch" | "showcase" | "price";
const IDLE_NUDGE_MS = 5000;

export function DemoScreen() {
  const reduced = useReducedMotion();
  const [concept] = useState(() => new URLSearchParams(window.location.search).get("concept"));
  // Meadow is the page (Alan 2026-10-01: the click-to-paste lane); Steel stays reviewable at ?concept=steel.
  const isMeadow = concept !== "steel";
  const reviewing = concept === "meadow" || concept === "steel";
  const [phase, setPhase] = useState<Phase>("intake");
  const [intake, setIntake] = useState<IntakeState>(() => readSaved().draft ?? { brief: "", productUrl: "", noProduct: false });
  const [check, setCheck] = useState<"idle" | "checking" | "done">("idle");
  const [found, setFound] = useState<Intake>({ productUrl: null, language: null, niche: null, briefKind: null });
  const [fileNote, setFileNote] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [nudge, setNudge] = useState(false);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [fail, setFail] = useState<Fail | null>(null);
  const [network, setNetwork] = useState<Network | null>(null);
  const [paused, setPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [seconds, setSeconds] = useState(45);
  const fileRef = useRef<HTMLInputElement>(null);
  const [intro, setIntro] = useState<"loading" | "opening" | "done">(reduced || isMeadow ? "done" : "loading");
  const [art, setArt] = useState({ tray: false, cloche: false });
  useEffect(() => {
    if (reduced) { setIntro("done"); return; }
    if (intro === "loading" && art.tray && art.cloche) setIntro("opening");
  }, [art, intro, reduced]);
  useEffect(() => {
    if (intro === "done") return;
    const timer = setTimeout(() => setIntro("done"), intro === "loading" ? 2500 : 1800);
    return () => clearTimeout(timer);
  }, [intro]);

  // Preserve the draft (Hayes: preserve work). Never blocks.
  useEffect(() => { save({ draft: intake }); }, [intake]);

  // "Don't be shy :)" after 5 s of an empty, untouched field.
  useEffect(() => {
    setNudge(false);
    if (phase !== "intake" || intake.brief.trim()) return;
    const t = setTimeout(() => setNudge(true), IDLE_NUDGE_MS);
    return () => clearTimeout(t);
  }, [phase, intake.brief]);

  // ---- intake check: debounced, only on a real change, never blocking the button
  const lastChecked = useRef("");
  useEffect(() => {
    const brief = intake.brief.trim();
    if (brief.length < 10 || brief === lastChecked.current) return;
    const ctl = new AbortController();
    const t = setTimeout(async () => {
      lastChecked.current = brief;
      setCheck("checking");
      try {
        const r = await publicPost<Intake>("/api/public/pricing/intake-check", { raw: brief }, ctl.signal);
        if (r.status === 200 && r.body) {
          setFound(r.body);
          const url = r.body.productUrl;
          if (url) setIntake((s) => (s.productUrl ? s : { ...s, productUrl: url }));
        }
      } catch { /* aborted or offline: the field simply asks */ }
      if (!ctl.signal.aborted) setCheck("done");
    }, 700);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [intake.brief]);

  // ---- file → text through the existing extractor
  const readFile = useCallback(async (file: File) => {
    setFileNote(`Reading ${file.name}…`);
    const r = await fileText(file);
    if ("text" in r) {
      setIntake((s) => ({ ...s, brief: s.brief.trim() ? `${s.brief.trim()}\n\n${r.text}` : r.text }));
      setFileNote(file.name);
    } else {
      setFileNote(r.message);
    }
  }, []);

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) { void readFile(f); return; }
    const text = e.dataTransfer.getData("text/plain");
    if (text) setIntake((s) => ({ ...s, brief: s.brief.trim() ? `${s.brief.trim()}\n\n${text}` : text }));
  };

  // ---- the quote + the network, started together on tap
  const [startedAt, setStartedAt] = useState(0);
  const lastBody = useRef<Body | null>(null);
  /** The request alone. A retry calls only this, so the showcase does not start over. */
  const runQuote = useCallback(async (body: Body) => {
    setFail(null);
    try {
      const r = await publicPost<Quote & { message?: string }>("/api/public/pricing/quote", body);
      if (r.status === 200 && r.body) {
        if (r.body.status === "quoted") {
          setQuote(r.body);
          save({ quote: { raw: body.raw, productUrl: body.productUrl ?? null, token: r.body.quoteToken, totalCents: r.body.totalCents, withContent: false } });
        } else {
          setPhase("intake");
          setFail({ kind: "input", message: r.body.message ?? "Add the spoken script, or estimate by length." });
        }
      } else if (r.status === 429) {
        setFail({ kind: "busy", message: r.body?.message ?? "Busy right now. Estimate by length instead." });
      } else if (r.status >= 400 && r.status < 500) {
        setPhase("intake");
        setFail({ kind: "input", message: r.body?.message ?? "Check the brief and try again." });
      } else {
        setFail({ kind: "down", message: "Couldn't price automatically. Try again, or estimate by length." });
      }
    } catch {
      setFail({ kind: "down", message: "You're offline. Your brief is saved on this device." });
    }
  }, []);

  const startQuote = useCallback((body: Body) => {
    lastBody.current = body;
    setQuote(null);
    setElapsed(0);
    setStartedAt(performance.now());
    setPhase(reduced ? "showcase" : "launch");
    const qs = new URLSearchParams();
    if (found.language) qs.set("language", found.language);
    if (found.niche) qs.set("niche", found.niche);
    void fetch(`/api/public/demo/network?${qs}`)
      .then((r) => (r.ok ? r.json() : null)).then((n) => setNetwork(n)).catch(() => setNetwork(null));
    void runQuote(body);
  }, [found.language, found.niche, runQuote, reduced]);

  // Cancel departure if a fast invalid-brief response returns to intake.
  useEffect(() => {
    if (phase !== "launch") return;
    const timer = setTimeout(() => { setElapsed(0); setStartedAt(performance.now()); setPhase("showcase"); }, isMeadow ? MEADOW_LAUNCH_MS : LAUNCH_MS);
    return () => clearTimeout(timer);
  }, [phase, isMeadow]);

  // ---- clock: pauses with the button and with a hidden tab
  useEffect(() => {
    if (phase !== "showcase") return;
    let acc = elapsed;
    let from = performance.now();
    const hidden = () => document.visibilityState === "hidden";
    const id = setInterval(() => {
      const now = performance.now();
      if (!paused && !hidden()) acc += now - from;
      from = now;
      setElapsed(acc);
    }, 50);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, paused, startedAt]);

  const state = clock(elapsed, quote !== null, reduced);
  useEffect(() => {
    if (phase === "showcase" && state.reveal) setPhase("price");
  }, [phase, state.reveal]);

  const mPhase: MatchPhase = state.stage === "match" ? matchPhase(state.progress) : state.stage === "guard" ? "rank" : "spin";
  const qualified = network?.stages[network.stages.length - 1]?.keptIds.length ?? 0;
  const line = stageLine(
    state.stage,
    quote ? { ai: quote.ai?.effort ?? null } : null,
    network ? { phase: mPhase, total: network.editors.length, qualified } : null,
    state.holding,
  );
  // Stages crossfade: the one leaving stays mounted for CROSSFADE_MS while the next fades in.
  const [leaving, setLeaving] = useState<StageKey | null>(null);
  const lastStage = useRef<StageKey>(state.stage);
  useEffect(() => {
    if (lastStage.current === state.stage) return;
    setLeaving(lastStage.current);
    lastStage.current = state.stage;
    const t = setTimeout(() => setLeaving(null), CROSSFADE_MS);
    return () => clearTimeout(t);
  }, [state.stage]);
  const briefTitle = found.productUrl ? hostOf(found.productUrl) : (intake.brief.split(/\n/).find((l) => l.trim()) ?? "").slice(0, 42);

  const submit = () => { if (canQuote(intake)) startQuote({ ...quoteRequest(intake), withContentFare: true }); };
  /** Meadow: the pasted script (trimmed once, the string the token binds) goes straight into the quote. */
  const pasted = (raw: string) => {
    setFail(null);
    setIntake({ brief: raw, productUrl: "", noProduct: true });
    startQuote({ raw, withContentFare: true });
  };
  // A brief handed over from /pricing ("watch it in the live demo") runs once, as if pasted.
  useEffect(() => {
    if (intro !== "done" || phase !== "intake") return;
    const handoff = readSaved().handoff;
    if (typeof handoff !== "string") return;
    save({ handoff: null });
    if (handoff.trim().length >= MIN_BRIEF) pasted(handoff.trim());
  }, [intro]); // eslint-disable-line react-hooks/exhaustive-deps
  const productOpen = check === "done" && !found.productUrl && !intake.noProduct && intake.brief.trim().length >= 10;
  const edit = () => { setPhase("intake"); setQuote(null); setFail(null); };

  return (
    <div className={`dm dm-concept-${isMeadow ? "meadow" : "steel"} dm-intro-${intro} dm-is-${phase}${paused ? " is-paused" : ""}${reduced ? " is-reduced" : ""}`} data-screen="demo">
      {isMeadow && <div className="dm-glow" aria-hidden="true" />}
      <header className="dm-head">
        <img src="/aditor-logo.png" alt="Aditor" className="dm-logo" />
        {reviewing && phase === "intake" && <nav className="dm-concepts" aria-label="Design concepts">
          <a href="?concept=meadow" aria-current={isMeadow ? "page" : undefined}>Meadow</a>
          <a href="?concept=steel" aria-current={!isMeadow ? "page" : undefined}>Steel</a>
        </nav>}
        {intro !== "done" && <button type="button" className="dm-quiet dm-skip" onClick={() => setIntro("done")}>Skip intro</button>}
        {(phase === "showcase" || phase === "price") && (
          <div className="dm-progress" role="progressbar" aria-label="Pricing your brief" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round((phase === "price" ? 1 : state.bar) * 100)}>
            <div className="dm-progress-track"><i style={{ transform: `scaleX(${phase === "price" ? 1 : state.bar})` }} /></div>
            <span>{phase === "price" ? "Priced" : state.holding ? "Almost there" : `Price in ~${Math.max(1, state.secondsLeft)} s`}</span>
          </div>
        )}
      </header>

      {(phase === "intake" || phase === "launch") && (
        <main className={`dm-stage dm-intake${phase === "launch" ? " is-launching" : ""}`} aria-busy={phase === "launch"}>
          <h1 className="dm-h1">{isMeadow ? <>Uber for <span>Ads.</span></> : <>Uber for <em>Ads</em></>}</h1>
          <p className="dm-sub">{isMeadow ? "Got a script? We'll show you how it works." : "Watch how it works on your next script or briefing."}</p>

          {isMeadow ? <PasteEntry onText={pasted} busy={phase !== "intake"} /> : <>
          <div className="dm-service">
            {!isMeadow && <img className="dm-tray" src={tray} alt="" aria-hidden="true" onLoad={() => setArt((a) => ({ ...a, tray: true }))} onError={() => setIntro("done")} />}
            {intro !== "done" && <img className="dm-cloche" src={cloche} alt="" aria-hidden="true" onLoad={() => setArt((a) => ({ ...a, cloche: true }))} onError={() => setIntro("done")} />}
          <div
            className={`dm-prompt${dragging ? " is-drag" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false); }}
            onDrop={onDrop}
          >
            <label className="dm-sr" htmlFor="dm-brief">Briefing or script</label>
            <textarea
              id="dm-brief"
              value={intake.brief}
              placeholder={nudge ? "Don't be shy :)" : "Paste a script, drop a PDF, or add a Google Doc link"}
              onChange={(e) => { setIntake((s) => ({ ...s, brief: e.target.value })); setFail(null); }}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
            />
            <div className="dm-prompt-foot">
              <button type="button" className="dm-file-link" onClick={() => fileRef.current?.click()}>Add attachment</button>
              <input ref={fileRef} type="file" hidden accept=".pdf,.docx,.txt,.md,.html" onChange={(e) => { const f = e.target.files?.[0]; if (f) void readFile(f); e.target.value = ""; }} />
              <span className="dm-bar-note" aria-live="polite">
                {fileNote ?? (check === "checking" ? "Looking for a product link…" : found.productUrl && !intake.noProduct ? <>Product · {hostOf(found.productUrl)} <b>✓</b></> : "")}
              </span>
              <button type="button" className="dm-cta dm-quote" disabled={!canQuote(intake)} onClick={submit}>Get quote<span aria-hidden="true"> →</span></button>
            </div>
            {dragging && <div className="dm-drop" aria-hidden="true">Drop it — we'll read it</div>}
          </div>
          </div>

          <div className={`dm-product${productOpen ? " is-open" : ""}`} aria-hidden={!productOpen}>
            <div>
              <label htmlFor="dm-product">Which product is this for?</label>
              <input
                id="dm-product"
                inputMode="url"
                tabIndex={productOpen ? 0 : -1}
                placeholder="yourbrand.com/products/…"
                value={intake.productUrl}
                onChange={(e) => setIntake((s) => ({ ...s, productUrl: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                aria-invalid={intake.productUrl !== "" && normalizeUrl(intake.productUrl) === null}
              />
              <button type="button" className="dm-quiet" tabIndex={productOpen ? 0 : -1} onClick={() => setIntake((s) => ({ ...s, noProduct: true }))}>No product page</button>
            </div>
          </div>
          </>}

          {fail && <p className="dm-error" role="alert">{fail.message}</p>}
          {fail?.kind === "input" && <LengthFallback seconds={seconds} setSeconds={setSeconds} onGo={() => startQuote({ raw: "", seconds })} />}
          {!isMeadow && <p className="dm-foot">Your brief stays private. Nothing is ordered yet.</p>}
        </main>
      )}

      {(phase === "showcase" || phase === "price") && (
        <main className="dm-stage dm-show">
          <section className={`dm-theatre${phase === "price" ? " is-behind" : ""}`} aria-live="polite">
            <div className="dm-scenes">
              {(["scan", "measure", "match", "guard"] as StageKey[]).map((k) => (k === state.stage || k === leaving) && (
                <div key={k} className={`dm-scene${k === state.stage ? " is-on" : " is-off"}`}>
                  {k === "scan" && <ScanScene found={found} quote={quote} brief={intake.brief} />}
                  {k === "measure" && <MeasureScene quote={quote} brief={intake.brief} paused={paused} reduced={reduced} />}
                  {k === "match" && (network ? <MatchScene network={network} phase={mPhase} progress={state.stage === "match" ? state.progress : 1} paused={paused} reduced={reduced} /> : null)}
                  {k === "guard" && <GuardScene holding={state.holding} />}
                </div>
              ))}
            </div>
            <p className="dm-line" key={line}>{line}</p>
            {fail && phase === "showcase" && (
              <div className="dm-fail" role="alert">
                <p>{fail.message}</p>
                <div className="dm-fail-row">
                  {fail.kind === "down" && <button type="button" className="dm-cta" onClick={() => lastBody.current && void runQuote(lastBody.current)}>Try again</button>}
                  <button type="button" className="dm-quiet" onClick={() => { setPhase("intake"); setFail({ kind: "input", message: "Estimate by length instead:" }); }}>Estimate by length</button>
                </div>
              </div>
            )}
          </section>
          {phase === "price" && quote && <PriceCard quote={quote} reduced={reduced} briefTitle={briefTitle} onEdit={edit} />}
        </main>
      )}

      <footer className="dm-footer">
        <span>Aditor · On-demand ad production</span>
        {phase === "showcase" && <button type="button" className="dm-quiet" onClick={() => setPaused((p) => !p)}>{paused ? "Resume animation" : "Pause animation"}</button>}
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------- paste entry (Meadow)

/** File → text through the existing /document extractor. */
async function fileText(file: File): Promise<{ text: string } | { message: string }> {
  if (file.size > 5 * 1024 * 1024) return { message: "Choose a file smaller than 5 MB." };
  const data = await new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result).split(",")[1] ?? "");
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(file);
  }).catch(() => "");
  const r = await publicPost<{ text?: string; message?: string }>("/api/public/pricing/document", { name: file.name, data }).catch(() => null);
  return r?.status === 200 && r.body?.text ? { text: r.body.text } : { message: r?.body?.message ?? "Couldn't read that file. Paste the text instead." };
}

const SOURCES = [
  { name: "Apple Notes", icon: notesIcon, help: "In Notes, select the text, copy it, and paste it here. Shared-note invitations can't be read." },
  { name: "Notion", icon: notionIcon, help: "Copy the page's text and paste it here, or upload an export." },
  { name: "Google Docs", icon: docsIcon, help: "Paste the Doc's link if it's public, or copy its text. Private Docs work as copied text or an export." },
] as const;
const isField = (t: EventTarget | null) => t instanceof Element && t.closest("input, textarea, select, [contenteditable]:not([contenteditable='false'])") !== null;

/**
 * One tap pastes. The clipboard is read inside the gesture; when the browser
 * keeps it, an inline field takes over (no dialog). ⌘V anywhere on the page
 * works too. A haptic tick where the platform has one (iOS 18+: a native
 * switch toggled in the same gesture).
 */
function PasteEntry({ onText, busy }: { onText: (raw: string) => void; busy: boolean }) {
  const [help, setHelp] = useState<string | null>(null);
  const [asking, setAsking] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const [touch] = useState(() => typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches);
  const fileRef = useRef<HTMLInputElement>(null);
  const hapticRef = useRef<HTMLLabelElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const haptic = () => { try { navigator.vibrate?.(14); } catch { /* unsupported */ } hapticRef.current?.click(); };
  /** What the button says back in the second between paste and departure. */
  const [got, setGot] = useState<{ words: number; hook: string } | null>(null);
  useEffect(() => { if (!busy) setGot(null); }, [busy]);
  const accept = (value: string) => {
    const raw = value.trim();
    if (raw.length < MIN_BRIEF) { setText(value); setAsking(raw ? "That's too short to price. Paste the whole script." : "Your clipboard is empty."); return; }
    setAsking(null);
    const first = raw.split("\n").find((l) => l.trim()) ?? "";
    setGot({ words: raw.split(/\s+/).length, hook: first.replace(/^[A-Za-z ]{2,12}:\s*/, "").slice(0, 72) });
    onText(raw);
  };
  const paste = () => {
    if (busy) return;
    haptic();
    const read = window.isSecureContext && navigator.clipboard?.readText ? navigator.clipboard.readText() : null;
    if (!read) { setAsking("This browser keeps the clipboard to itself."); return; }
    read.then(accept, () => setAsking("Your browser kept the clipboard private."));
  };
  useEffect(() => {
    if (busy) return;
    const onPaste = (e: ClipboardEvent) => {
      if (isField(e.target)) return;
      const value = e.clipboardData?.getData("text/plain") ?? "";
      e.preventDefault();
      haptic();
      accept(value);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  });
  useEffect(() => { if (asking) field.current?.focus({ preventScroll: true }); }, [asking]);
  const cancel = () => { setAsking(null); setText(""); btn.current?.focus({ preventScroll: true }); };
  const ready = text.trim().length >= MIN_BRIEF;
  return (
    <div className="dm-paste">
      <div className="dm-sources" role="group" aria-label="Works with">
        {SOURCES.map((src, n) => (
          <button key={src.name} type="button" className="dm-source" style={{ "--n": n } as CSSProperties} aria-label={src.name} title={src.name} aria-expanded={help === src.name} aria-controls="dm-source-help"
            onClick={() => setHelp((h) => (h === src.name ? null : src.name))}>
            <img src={src.icon} alt="" width="32" height="32" />
          </button>
        ))}
      </div>
      <p id="dm-source-help" className="dm-source-help" aria-live="polite">{help ? SOURCES.find((src) => src.name === help)!.help : note ?? ""}</p>
      <div className="dm-paste-slot">
      {got ? (
        <>
          <p className="dm-slip" aria-hidden="true">{got.hook}</p>
          <button type="button" className="dm-paste-btn is-received" disabled>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 12.5 4 4 8-9" /></svg>
            <span>Pasted · {got.words} words</span>
          </button>
        </>
      ) : asking ? (
        <form className="dm-pfield" onSubmit={(e) => { e.preventDefault(); if (ready) accept(text); }}>
          <p id="dm-pfield-why">{asking}</p>
          <div className="dm-pfield-row">
            <textarea ref={field} aria-label="Script or Google Docs link" aria-describedby="dm-pfield-why" rows={text ? Math.min(5, text.split("\n").length) : 1}
              value={text} placeholder={touch ? "Tap here, then Paste" : "Press ⌘V to drop it in"}
              onChange={(e) => setText(e.target.value)}
              onPaste={(e) => { const v = e.clipboardData.getData("text/plain"); if (v.trim() && !text.trim()) { e.preventDefault(); haptic(); accept(v); } }}
              onKeyDown={(e) => { if (e.key === "Escape") cancel(); if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && ready) accept(text); }} />
            {ready ? <button type="submit" className="dm-pchip is-go">Continue</button> : <button type="button" className="dm-pchip" onClick={cancel}>Cancel</button>}
          </div>
        </form>
      ) : (
        <button ref={btn} type="button" className="dm-paste-btn" onClick={paste} disabled={busy}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="4.5" width="14" height="16.5" rx="2.5" /><rect x="8.5" y="3" width="7" height="3.2" rx="1.2" /></svg>
          <span>{touch ? "Tap to paste" : "Click to paste"}</span>
        </button>
      )}
      </div>
      <button type="button" className="dm-file-link dm-paste-file" onClick={() => fileRef.current?.click()}>or upload a file</button>
      <input ref={fileRef} type="file" hidden accept=".pdf,.docx,.txt,.md,.html" onChange={(e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        setNote(`Reading ${f.name}…`);
        void fileText(f).then((r) => { if ("text" in r) { setNote(null); accept(r.text); } else setNote(r.message); });
      }} />
      <label ref={hapticRef} className="dm-sr" aria-hidden="true"><input type="checkbox" tabIndex={-1} {...{ switch: "" }} /></label>
    </div>
  );
}

// ---------------------------------------------------------------- pieces

/** Line icons, 24 px, currentColor — one glyph per idea, so the scenes need almost no words. */
const Icon = {
  film: <svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="3" /><path d="M10 9.5v5l4-2.5z" /></svg>,
  tag: <svg viewBox="0 0 24 24"><path d="M3 12V4h8l10 10-8 8z" /><circle cx="7.5" cy="8.5" r="1.3" /></svg>,
  speech: <svg viewBox="0 0 24 24"><path d="M4 5h16v11H9l-5 4z" /></svg>,
  clock: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></svg>,
};

/** One script line, its label ("HOOK:", "VO:") picked out the way an editor reads it. */
function ScriptLine({ line }: { line: string }) {
  const m = /^([A-Za-z][A-Za-z ]{0,11}):\s*(.*)$/.exec(line);
  return <p>{m ? <><b>{m[1]}</b> {m[2]}</> : line}</p>;
}

/**
 * The pasted script becomes a box and is read (Alan 2026-10-01): a beam runs down
 * it, the lines it has passed light up, then what we found pops out below.
 */
function ScanScene({ found, quote, brief }: { found: Intake; quote: Quote | null; brief: string }) {
  const lang = found.language ?? quote?.language ?? null;
  const pills: Array<[keyof typeof Icon, string | null]> = [
    ["film", quote ? (quote.scope?.includes("Static") ? "Static" : "Video") : null],
    ["tag", found.niche ? NICHE_CHIP[found.niche] ?? null : null],
    ["speech", lang === "de" ? "German" : lang === "en" ? "English" : null],
  ];
  const lines = brief.split("\n").map((l) => l.trim()).filter(Boolean).slice(0, 8);
  return (
    <div className="dm-scanscene">
      <div className="dm-doc" aria-hidden="true">
        <div className="dm-doc-head"><img src={emojiPage} alt="" width="28" height="28" /><span>Your script</span></div>
        <div className="dm-doc-body">
          <div className="dm-doc-text">{lines.map((l, i) => <ScriptLine key={i} line={l} />)}</div>
          <div className="dm-doc-text is-read">{lines.map((l, i) => <ScriptLine key={i} line={l} />)}</div>
          <i className="dm-beam" />
          <img className="dm-lens" src={emojiMagnifier} alt="" />
        </div>
      </div>
      <div className="dm-pills">
        {pills.filter(([, v]) => v).map(([icon, v], i) => (
          <span key={icon} className="dm-ipill" style={{ animationDelay: `${1350 + i * 160}ms` }}>
            {Icon[icon]}<b>{v}</b>
          </span>
        ))}
      </div>
    </div>
  );
}

/** Codex Round 2 R1: one ~2.6 s clip per AI effort, ends level (= its poster). */
const SCALE = {
  light: { src: scaleLight, poster: scaleLightPoster },
  medium: { src: scaleMedium, poster: scaleMediumPoster },
  heavy: { src: scaleHeavy, poster: scaleHeavyPoster },
} as const;

/**
 * The scale (Alan 2026-10-01): generated frames tip it, our tokens balance it, it
 * sways and settles level — the AI work reads as covered, never as a number
 * (feedback_upsell_only_the_saving). The clip is illustrative, never the customer's output.
 */
function MeasureScene({ quote, brief, paused, reduced }: { quote: Quote | null; brief: string; paused: boolean; reduced: boolean }) {
  const { concept, effort, count } = productionPreview(brief, quote?.ai?.effort ?? null);
  // Picked once: swapping the clip when the quote lands would restart it mid-sway.
  const [clip] = useState(() => SCALE[effort ?? (count >= 9 ? "heavy" : count >= 6 ? "medium" : "light")]);
  const video = useRef<HTMLVideoElement>(null);
  // iOS Low Power Mode refuses autoplay and paints a play button over the clip: show the still instead.
  const [still, setStill] = useState(false);
  useEffect(() => {
    const v = video.current;
    if (!v) return;
    if (paused) v.pause(); else void v.play().catch(() => setStill(true));
  }, [paused]);
  return (
    <div className="dm-production" data-effort={effort ?? "pending"}>
      {reduced || still
        ? <img className="dm-scale-clip" src={clip.poster} alt="" aria-hidden="true" />
        : <video ref={video} className="dm-scale-clip" src={clip.src} poster={clip.poster} muted playsInline autoPlay preload="auto" aria-hidden="true" />}
      <p className="dm-covered"><svg viewBox="0 0 24 24"><path d="m6 12.5 4 4 8-9" /></svg>AI tokens covered</p>
      <div className="dm-effort-kinds">{["VSL", "UGC", "Animated"].map((kind, i) => <span key={kind} className={concept === kind || (!concept && effort === ["light", "medium", "heavy"][i]) ? "is-on" : ""}>{kind}<i /></span>)}</div>
      <p className="dm-example-note">Illustrative previews</p>
    </div>
  );
}

/** What each filter on the slider means, in the brand's words. */
const FILTER_LABEL: Partial<Record<ChainStage["key"], string>> = {
  niche: "Cut your niche before",
  language: "Speaks your language",
  trained: "Trained on our playbook",
  available: "Free this week",
};

/**
 * The editor match (Alan 2026-10-01): everyone arrives on the globe as one wave;
 * a hand closes a range slider from both sides, filter by filter, and the editors
 * who don't fit drop off the map — we take our time picking. Then the hand taps
 * the top three open on the globe. No made-up skill bars.
 */
function MatchScene({ network, phase, progress, paused, reduced }: { network: Network; phase: MatchPhase; progress: number; paused: boolean; reduced: boolean }) {
  const filters = useMemo(() => network.stages.filter((st) => st.key !== "network" && st.key !== "ranked"), [network]);
  const ranked = network.stages[network.stages.length - 1]?.keptIds ?? [];
  const range = rangeAt(progress, filters.length);
  const everyone = useMemo(() => new Set(network.editors.map((e) => e.id)), [network]);
  const kept = filters[range.applied - 1];
  const active = useMemo(() => (kept ? new Set(kept.keptIds) : everyone), [kept, everyone]);
  const openIds = useMemo(() => (phase === "rank" ? ranked.slice(0, 3) : []), [phase, ranked]);
  const size = typeof window !== "undefined" && window.innerWidth < 768 ? 270 : 360;
  // The label names the filter the hand is pulling in (or the last one once it rests).
  const pulling = filters[Math.min(filters.length - 1, range.pressing ? range.applied : Math.max(0, range.applied - 1))];
  const done = phase === "rank";
  return (
    <div className="dm-match">
      <Globe editors={network.editors} activeIds={active} openIds={openIds} paused={paused} reduced={reduced} size={size} />
      <div className="dm-count"><b key={active.size}>{active.size}</b><span>{kept ? "fit" : "editors"}</span></div>
      {filters.length > 0 && (
        <div className={`dm-filter${range.pressing ? " is-press" : ""}${phase === "spin" ? "" : " is-on"}${done ? " is-done" : ""}`} aria-hidden="true"
          style={{ "--lo": range.lo, "--hi": range.hi, "--h": range.hand } as CSSProperties}>
          <p className="dm-filter-label" key={pulling?.key}>{pulling ? FILTER_LABEL[pulling.key] : ""}</p>
          <div className="dm-filter-rail">
            <i className="dm-filter-fill" />
            <div className="dm-filter-run is-lo"><span className="dm-filter-knob" /></div>
            <div className="dm-filter-run is-hi"><span className="dm-filter-knob" /></div>
            <div className="dm-filter-run is-hand"><img className="dm-hand" src={pointingHand} width={56} height={56} alt="" aria-hidden="true" draggable={false} /></div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The review shield (Alan 2026-10-01, calmer the same day): a draft drifts in, is turned back once — the
 * revision the owner never sees. Once it's right it passes through, lands with
 * the brand owner in deep work, the shield switches off and a green check stands
 * where it was.
 */
function GuardScene({ holding }: { holding: boolean }) {
  return (
    <div className={`dm-guard${holding ? " is-holding" : ""}`}>
      <div className="dm-guard-stage">
        <img className="dm-shot" src={emojiPage} alt="" aria-hidden="true" />
        <img className="dm-final-file" src={emojiReady} alt="" aria-hidden="true" />
        <div className="dm-barrier" aria-hidden="true">
          <img src={autoReviewIcon} alt="" />
          <i />
        </div>
        <div className="dm-pass" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="m6 12.5 4 4 8-9" /></svg><span>Reviewed</span></div>
        <div className="dm-owner"><img className="dm-owner-img" src={owner} alt="" /></div>
      </div>
    </div>
  );
}

type FolderState =
  | { status: "idle" | "checking" }
  | { status: "ok"; url: string; files: number }
  | { status: "invalid" | "unreadable" | "empty" | "not_checked" | "error"; url: string | null };

const FOLDER_MESSAGE: Record<string, string> = {
  invalid: "Paste the share link of a Google Drive folder.",
  unreadable: "We can't open this folder. Set sharing to “Anyone with the link can view”.",
  empty: "This folder is empty — add your shots or footage, then try again.",
  not_checked: "We can't look inside folders right now. Keep the link — we'll check it after you confirm.",
  error: "Couldn't check the folder. Try again.",
};

function PriceCard({ quote, reduced, briefTitle, onEdit }: { quote: Quote; reduced: boolean; briefTitle: string; onEdit: () => void }) {
  const [open, setOpen] = useState(false);
  const [folderUrl, setFolderUrl] = useState("");
  const [folder, setFolder] = useState<FolderState>({ status: "idle" });
  // The own-material price only exists once we have actually read their folder.
  const withContent = folder.status === "ok";
  const checkFolder = async (value = folderUrl) => {
    if (!value.trim()) return;
    setFolder({ status: "checking" });
    try {
      const r = await publicPost<{ status: FolderState["status"]; url: string | null; files: number }>("/api/public/pricing/content-folder", { url: value.trim() });
      const b = r.body;
      if (b?.status === "ok" && b.url) setFolder({ status: "ok", url: b.url, files: b.files });
      else setFolder({ status: (b?.status ?? "error") as "invalid", url: b?.url ?? null });
    } catch {
      setFolder({ status: "error", url: null });
    }
  };
  const target = withContent && quote.contentOffer ? quote.contentOffer.totalCents : quote.totalCents ?? 0;
  const range = quote.totalMinCents !== undefined && quote.totalMaxCents !== undefined;
  const [shown, setShown] = useState(reduced ? target : 0);
  const shownRef = useRef(shown);
  shownRef.current = shown;
  useEffect(() => {
    if (reduced) { setShown(target); return; }
    const from = shownRef.current;
    const t0 = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / 600);
      setShown(Math.round(from + (target - from) * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, reduced]);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => heading.current?.focus({ preventScroll: true }), []);
  useEffect(() => {
    const token = withContent && quote.contentOffer ? quote.contentOffer.quoteToken : quote.quoteToken;
    save({
      quote: { ...(readSaved().quote ?? {}), token, totalCents: target, withContent },
      // B attaches this as the brand's content folder (onboarding content-folder step).
      contentFolderUrl: folder.status === "ok" || folder.status === "not_checked" ? folder.url : null,
    });
  }, [withContent, quote, target, folder]);
  // Apple Pay sheet (Alan 2026-10-01): label left, value right, hairlines, the total last.
  const rows: [string, string][] = [
    ["Video", [quote.seconds ? mmss(Math.round(quote.seconds)) : quote.scope, quote.variants ? `${quote.variants + 1} hooks` : null].filter(Boolean).join(" · ")],
    ...(quote.ai ? [["AI generation", quote.ai.effort[0]!.toUpperCase() + quote.ai.effort.slice(1)] as [string, string]] : []),
    ...(!range ? [["Ready", "in 48 h"] as [string, string]] : []),
    ...(quote.noChargeUntilCents ? [["Due today", eur(0)] as [string, string]] : []),
  ];
  const lookRight = () => {
    // B builds Cheers → card on this saved quote; until then the existing signup takes over.
    window.location.assign("/onboarding");
  };
  return (
    <section className="dm-price" aria-labelledby="dm-price-h">
      <i className="dm-grabber" aria-hidden="true" />
      {/* The brief flew out of view during the climb; only its title stays. */}
      <header className="dm-pay-head">
        <img src="/aditor-logo.png" alt="" />
        <div><b>Aditor</b><span>Your brief · {briefTitle}</span></div>
        <button type="button" className="dm-inline-link" onClick={onEdit}>Edit</button>
      </header>
      <dl className="dm-pay-rows">
        {rows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
      </dl>
      <div className="dm-pay-total">
        <span>Total</span>
        <h2 id="dm-price-h" ref={heading} tabIndex={-1} className="dm-amount">
          {range ? `${eur(quote.totalMinCents!)}–${eur(quote.totalMaxCents!)}` : eur(shown)}
        </h2>
      </div>
      <button type="button" className="dm-cta dm-cta-lg dm-pay-btn" onClick={lookRight}>Looks right, let's go!</button>
      <p className="dm-promise">
        <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2.5" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
        Price locked once confirmed{quote.noChargeUntilCents ? ` · no charge until you've spent ${eur(quote.noChargeUntilCents)}` : ""}
      </p>
      {quote.contentOffer && (
        <div className={`dm-reduce${open ? " is-open" : ""}`}>
          <button type="button" className="dm-reduce-toggle" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            Ways to reduce your fare <span aria-hidden="true">⌄</span>
          </button>
          <div className="dm-reduce-body">
            <div>
              <p>Have product shots, footage or a brand content folder? Share the Google Drive link and we skip the AI generation.</p>
              <div className={`dm-folder${withContent ? " is-ok" : ""}`}>
                <label className="dm-sr" htmlFor="dm-folder">Google Drive folder link</label>
                <input
                  id="dm-folder"
                  inputMode="url"
                  tabIndex={open ? 0 : -1}
                  placeholder="drive.google.com/drive/folders/…"
                  value={folderUrl}
                  onChange={(e) => { setFolderUrl(e.target.value); if (folder.status !== "idle") setFolder({ status: "idle" }); }}
                  onPaste={(e) => { const t = e.clipboardData.getData("text"); if (t) { e.preventDefault(); setFolderUrl(t); void checkFolder(t); } }}
                  onKeyDown={(e) => { if (e.key === "Enter") void checkFolder(); }}
                  aria-describedby="dm-folder-note"
                />
                <button type="button" className="dm-folder-btn" tabIndex={open ? 0 : -1} disabled={!folderUrl.trim() || folder.status === "checking"} onClick={() => void checkFolder()}>
                  {folder.status === "checking" ? "Checking…" : withContent ? "✓" : "Check"}
                </button>
              </div>
              <small id="dm-folder-note" className={folder.status === "ok" ? "is-good" : folder.status === "idle" || folder.status === "checking" ? "" : "is-warn"} aria-live="polite">
                {folder.status === "ok"
                  ? `${folder.files} ${folder.files === 1 ? "file" : "files"} found — your price uses your content.`
                  : FOLDER_MESSAGE[folder.status] ?? "No content? No problem — we'll create it with AI."}
              </small>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function LengthFallback({ seconds, setSeconds, onGo }: { seconds: number; setSeconds: (n: number) => void; onGo: () => void }) {
  return (
    <div className="dm-length">
      <label htmlFor="dm-seconds">Finished length <b>{seconds} s</b></label>
      <input id="dm-seconds" type="range" min={10} max={180} step={5} value={seconds} onChange={(e) => setSeconds(Number(e.target.value))} />
      <button type="button" className="dm-cta" onClick={onGo}>Estimate by length</button>
    </div>
  );
}
